import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { clientesAPI } from '../../services/api';

const ClientesConfig = () => {
  const { showSuccess, showError, showConfirm } = useToast();
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    dni: '',
    celular: ''
  });

  // Cargar clientes
  useEffect(() => {
    loadClientes();
  }, []);

  // Filtrar clientes basado en el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredClientes(clientes);
    } else {
      const filtered = clientes.filter(cliente =>
        cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.dni.includes(searchTerm) ||
        (cliente.celular && cliente.celular.includes(searchTerm))
      );
      setFilteredClientes(filtered);
    }
  }, [searchTerm, clientes]);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await clientesAPI.getClientes();
      setClientes(response.data);
      setFilteredClientes(response.data);
    } catch (err) {
      setError('Error al cargar clientes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validaciones
      if (!formData.nombre || !formData.dni) {
        throw new Error('Nombre y DNI son obligatorios');
      }

      if (formData.dni.length !== 8) {
        throw new Error('El DNI debe tener 8 dígitos');
      }

      if (formData.celular && formData.celular.length !== 9) {
        throw new Error('El celular debe tener 9 dígitos');
      }

      if (editingCliente) {
        await clientesAPI.updateCliente(editingCliente.id, formData);
      } else {
        await clientesAPI.createCliente(formData);
      }
      
      setFormData({ nombre: '', dni: '', celular: '' });
      setEditingCliente(null);
      setShowForm(false);
      loadClientes();
    } catch (err) {
      setError('Error al guardar cliente: ' + err.message);
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nombre: cliente.nombre,
      dni: cliente.dni,
      celular: cliente.celular || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!await showConfirm('¿Estás seguro de que quieres eliminar este cliente?')) {
      return;
    }

    try {
      await clientesAPI.deleteCliente(id);
      loadClientes();
    } catch (err) {
      setError('Error al eliminar cliente: ' + err.message);
    }
  };

  const handleCancel = () => {
    setFormData({ nombre: '', dni: '', celular: '' });
    setEditingCliente(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Cargando clientes...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>👥 Gestión de Clientes</h3>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          + Nuevo Cliente
        </button>
      </div>

      {/* Barra de búsqueda */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar cliente por nombre, DNI, celular o dirección..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm('')}
                title="Limpiar búsqueda"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="text-muted">
            {searchTerm ? (
              <span>
                Mostrando {filteredClientes.length} de {clientes.length} clientes
              </span>
            ) : (
              <span>Total: {clientes.length} clientes</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5>{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="nombre" className="form-label">Nombre *</label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      className="form-control"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="dni" className="form-label">DNI *</label>
                    <input
                      type="text"
                      id="dni"
                      name="dni"
                      className="form-control"
                      value={formData.dni}
                      onChange={handleInputChange}
                      maxLength="8"
                      pattern="[0-9]{8}"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="celular" className="form-label">Celular</label>
                    <input
                      type="text"
                      id="celular"
                      name="celular"
                      className="form-control"
                      value={formData.celular}
                      onChange={handleInputChange}
                      maxLength="9"
                      pattern="[0-9]{9}"
                      placeholder="Opcional"
                    />
                  </div>
                </div>
              </div>
              
              <div className="d-flex gap-2 mt-3">
                <button type="submit" className="btn btn-primary">
                  {editingCliente ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de clientes */}
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>DNI</th>
              <th>Celular</th>
              <th>Expedientes</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredClientes.map((cliente) => (
              <tr key={cliente.id}>
                <td>{cliente.nombre}</td>
                <td>{cliente.dni}</td>
                <td>
                  {cliente.celular ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigator.clipboard.writeText(cliente.celular)}
                      title="Copiar celular"
                    >
                      {cliente.celular}
                    </button>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <span className="badge bg-info">
                    {cliente._count?.expedientes || 0}
                  </span>
                </td>
                <td>
                  <div className="btn-group btn-group-sm">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => handleEdit(cliente)}
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => handleDelete(cliente.id)}
                      disabled={cliente._count?.expedientes > 0}
                      title={cliente._count?.expedientes > 0 ? `No se puede eliminar porque tiene ${cliente._count?.expedientes} expediente(s) asociado(s)` : 'Eliminar cliente'}
                      style={{ 
                        position: 'relative', 
                        zIndex: 9999,
                        opacity: cliente._count?.expedientes > 0 ? 0.5 : 1,
                        cursor: cliente._count?.expedientes > 0 ? 'not-allowed' : 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (cliente._count?.expedientes > 0) {
                          e.target.title = `No se puede eliminar porque tiene ${cliente._count?.expedientes} expediente(s) asociado(s)`;
                        }
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                  {cliente._count?.expedientes > 0 && (
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#dc3545', 
                      marginTop: '2px',
                      fontStyle: 'italic'
                    }}>
                      ⚠️ No se puede eliminar (tiene {cliente._count?.expedientes} expediente{cliente._count?.expedientes > 1 ? 's' : ''})
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredClientes.length === 0 && (
        <div className="text-center text-muted py-4">
          {searchTerm ? 'No se encontraron clientes que coincidan con la búsqueda' : 'No hay clientes registrados'}
        </div>
      )}
    </div>
  );
};

export default ClientesConfig;


